import { memo } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Pagination from "@mui/material/Pagination";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import dayjs from "dayjs";
import SideBorderCard from "src-next/shared/ui/cards/SideBorderCard";
import type { MessageTemplate } from "../types";
import {
  formatTemplateDisplayName,
  getFluxmedDefaultTemplateCategory,
  getFluxmedDefaultCategoryLabel,
  getStatusUi,
  isMessageTemplateApproved,
  padraoFluxmedChipSx,
  templateStatusChipSx,
} from "../utils/messageTemplateUiUtils";
import {
  getFluxmedDefaultCategoryAccent,
  getFluxmedDefaultCategoryIcon,
} from "../utils/fluxmedDefaultTemplateCategoryIcons";
import { useTheme } from "@mui/material/styles";
import MessageTemplatePhonePreviewContent from "./MessageTemplatePhonePreviewContent";
import MessageTemplateCardIconGroup from "./MessageTemplateCardIconGroup";
import MessageDefaultTemplateUsageShortLine from "./MessageDefaultTemplateUsageShortLine";
import { getFluxmedDefaultTemplateUsageShortMessage } from "../utils/fluxmedDefaultTemplateUsage";

export interface MessageTemplatesCardsViewProps {
  templates: MessageTemplate[];
  organizationId?: string;
  totalPages: number;
  currentPage: number;
  onChangePage: (nextPage: number) => void;
  onEditTemplate: (template: MessageTemplate) => void;
  onDeleteTemplate: (templateId: string) => void;
  onSendTemplate: (template: MessageTemplate) => void;
  allowSend?: boolean;
  allowDelete?: boolean;
  allowEdit?: boolean;
  showDefaultTemplateBadge?: boolean;
  hasActiveMetaEndpoint?: boolean;
}

const MessageTemplatesCardsView = memo(
  ({
    templates,
    organizationId,
    totalPages,
    currentPage,
    onChangePage,
    onEditTemplate,
    onDeleteTemplate,
    onSendTemplate,
    allowSend = true,
    allowDelete = true,
    allowEdit = true,
    showDefaultTemplateBadge = false,
    hasActiveMetaEndpoint,
  }: MessageTemplatesCardsViewProps) => {
    const theme = useTheme();

    if (templates.length === 0) {
      return (
        <Paper
          variant="outlined"
          sx={{
            mt: 1.5,
            py: 4,
            px: 2,
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Nenhum modelo encontrado!
          </Typography>
        </Paper>
      );
    }

    return (
      <>
        <Box
          sx={{
            mt: 1.5,
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(3, minmax(0, 1fr))",
            },
          }}
        >
          {templates.map((template) => (
            (() => {
              const statusUi = getStatusUi(template.status);
              const canSendTemplate =
                allowSend && isMessageTemplateApproved(template.status);
              const displayName = formatTemplateDisplayName(template.name, {
                organizationPrefix: organizationId,
                hasActiveMetaEndpoint,
              });
              const templateCategory = showDefaultTemplateBadge
                ? getFluxmedDefaultTemplateCategory(
                    template.name,
                    organizationId,
                    hasActiveMetaEndpoint,
                  )
                : null;
              const CategoryIcon = templateCategory
                ? getFluxmedDefaultCategoryIcon(templateCategory)
                : null;
              const cardBorderColor =
                showDefaultTemplateBadge && templateCategory
                  ? getFluxmedDefaultCategoryAccent(templateCategory, theme).main
                  : theme.palette.primary.main;
              const usageShortMessage = showDefaultTemplateBadge
                ? getFluxmedDefaultTemplateUsageShortMessage(
                    template.name,
                    organizationId,
                    hasActiveMetaEndpoint,
                  )
                : null;

              return (
                <SideBorderCard
                  key={template.id}
                  borderColor={cardBorderColor}
                  role={canSendTemplate ? "button" : undefined}
                  tabIndex={canSendTemplate ? 0 : undefined}
                  aria-label={
                    canSendTemplate
                      ? `Enviar mensagem com o modelo ${displayName}`
                      : `Modelo ${displayName} — envio disponível apenas para modelos aprovados`
                  }
                  onClick={
                    canSendTemplate ? () => onSendTemplate(template) : undefined
                  }
                  onKeyDown={
                    canSendTemplate
                      ? (event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            onSendTemplate(template);
                          }
                        }
                      : undefined
                  }
                  sx={{
                    cursor: canSendTemplate ? "pointer" : "default",
                    transition: "box-shadow 0.2s ease",
                    ...(canSendTemplate
                      ? {
                          "&:hover": {
                            boxShadow: 4,
                          },
                          "&:focus-visible": {
                            outline: "2px solid",
                            outlineColor: "primary.main",
                            outlineOffset: 2,
                          },
                        }
                      : {}),
                  }}
                >
                  <Stack spacing={1.25}>
                    <Stack direction="row" alignItems="flex-start" spacing={1.25}>
                      <MessageTemplateCardIconGroup
                        CategoryIcon={
                          showDefaultTemplateBadge && CategoryIcon
                            ? CategoryIcon
                            : undefined
                        }
                        category={
                          showDefaultTemplateBadge && templateCategory
                            ? templateCategory
                            : undefined
                        }
                        categoryLabel={
                          showDefaultTemplateBadge && templateCategory
                            ? getFluxmedDefaultCategoryLabel(templateCategory)
                            : undefined
                        }
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" fontWeight={600} noWrap>
                          {formatTemplateDisplayName(template.name, {
                            organizationPrefix: organizationId,
                            hasActiveMetaEndpoint,
                          })}
                        </Typography>
                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                          <Typography variant="caption" color="text.secondary">
                            Modelo WhatsApp
                          </Typography>
                          {showDefaultTemplateBadge ? (
                            <Chip
                              label="Padrão Fluxmed"
                              size="small"
                              variant="outlined"
                              sx={padraoFluxmedChipSx}
                            />
                          ) : null}
                          <Chip
                            label={statusUi.label}
                            color={statusUi.color}
                            size="small"
                            variant="outlined"
                            sx={templateStatusChipSx}
                          />
                        </Stack>
                      </Box>
                      <Stack direction="row" spacing={0.25} sx={{ mt: -0.35 }}>
                        {allowSend ? (
                          <Tooltip
                            title={
                              canSendTemplate
                                ? "Enviar mensagem"
                                : "Envio permitido somente para modelos com status Aprovado"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                color="success"
                                disabled={!canSendTemplate}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  if (canSendTemplate) onSendTemplate(template);
                                }}
                                sx={{ borderRadius: 1.5 }}
                              >
                                <WhatsAppIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : null}
                        {allowEdit ? (
                          <Tooltip title="Editar modelo">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={(event) => {
                                event.stopPropagation();
                                onEditTemplate(template);
                              }}
                              sx={{ borderRadius: 1.5 }}
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                        {allowDelete ? (
                          <Tooltip title="Remover modelo">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(event) => {
                                event.stopPropagation();
                                onDeleteTemplate(template.id);
                              }}
                              sx={{ borderRadius: 1.5 }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </Stack>
                    </Stack>


                    <MessageTemplatePhonePreviewContent
                      isCard={true}
                      header={template.header}
                      headerFormat={template.headerFormat}
                      headerMediaUrl={template.headerMediaUrl}
                      headerMediaFileName={template.headerMediaFileName}
                      body={template.preview}
                      hasButton={Boolean(
                        template.buttonType && String(template.buttonText ?? "").trim(),
                      )}
                      buttonText={template.buttonText ?? ""}
                      buttonType={template.buttonType ?? ""}
                      footerTime={dayjs(template.createdAtIso).format("HH:mm")}
                    />

                    {usageShortMessage ? (
                      <MessageDefaultTemplateUsageShortLine message={usageShortMessage} />
                    ) : null}
                  </Stack>
                </SideBorderCard>
              );
            })()
          ))}
        </Box>

        <Stack direction="row" justifyContent="center" sx={{ mt: 1.5 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_event, nextPage) => onChangePage(nextPage)}
            color="primary"
            shape="rounded"
          />
        </Stack>
      </>
    );
  },
);

MessageTemplatesCardsView.displayName = "MessageTemplatesCardsView";

export default MessageTemplatesCardsView;
